import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  User,
  Calendar,
  Target,
  DollarSign,
  TrendingUp,
  ArrowRight,
  Edit
} from 'lucide-react';
import { Modal } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
} from '@/design-system';
import type { InvestibleStructure, ISCategory } from '@/types';
import { formatCurrencyCompact, formatPercent } from '@/utils/formatters';

interface InvestibleStructureDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  structure: InvestibleStructure | null;
  onEdit?: () => void;
}

const InvestibleStructureDetailModal: React.FC<InvestibleStructureDetailModalProps> = ({
  isOpen,
  onClose,
  structure,
  onEdit
}) => {
  const navigate = useNavigate();

  if (!structure) return null;

  const getCategoryBadgeVariant = (category: ISCategory): 'primary' | 'info' | 'success' => {
    switch (category) {
      case 'BUSINESS':
        return 'primary';
      case 'TECH':
        return 'info';
      case 'SUPPORT':
        return 'success';
      default:
        return 'primary';
    }
  };

  const getOwnerDisplayName = () => {
    if (structure.ownerType === 'USER' && structure.owner) {
      return `${structure.owner.firstName} ${structure.owner.lastName}`;
    } else if (structure.ownerType === 'TEXT' && structure.ownerText) {
      return structure.ownerText;
    }
    return 'Unassigned';
  };

  const calculateUtilization = (): number => {
    const demand = structure.demandTeamWeeks || 0;
    const supply = structure.supplyTeamWeeks || 0;
    if (demand === 0) return 0;
    return Math.min(100, (supply / demand) * 100);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewEpics = () => {
    // Navigate to EPICs page with IS filter
    navigate(`/epics?investibleStructureId=${structure.id}`);
    onClose();
  };

  const epicsCount = structure._count?.epics || 0;
  const utilization = calculateUtilization();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="lg"
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500/20 to-blue-500/20 border border-violet-500/30">
            <Building className="w-6 h-6 text-violet-500" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-xl font-semibold text-foreground">
                {structure.name}
              </h2>
              <GlassBadge variant={getCategoryBadgeVariant(structure.category)} size="sm">
                {structure.category}
              </GlassBadge>
            </div>
            {structure.planningPeriod && (
              <p className="text-sm text-muted-foreground/60 mt-1">
                {structure.planningPeriod.name}
              </p>
            )}
          </div>
        </div>

        {/* Description */}
        {structure.description && (
          <GlassCard variant="subtle" padding="md">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {structure.description}
            </p>
          </GlassCard>
        )}

        {/* Basic Information */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-wide mb-3">
            Basic Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <GlassCard variant="subtle" padding="sm">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-4 h-4 text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/60">Owner</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {getOwnerDisplayName()}
              </p>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/60">Planning Period</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {structure.planningPeriod?.name || 'Not assigned'}
              </p>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/60">Created</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {formatDate(structure.createdAt)}
              </p>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-muted-foreground/60" />
                <span className="text-xs text-muted-foreground/60">Last Updated</span>
              </div>
              <p className="text-sm font-medium text-foreground">
                {formatDate(structure.updatedAt)}
              </p>
            </GlassCard>
          </div>
        </div>

        {/* Capacity Overview */}
        <div>
          <h3 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-wide mb-3">
            Capacity Overview
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <GlassCard variant="subtle" padding="sm" className="text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="w-4 h-4 text-status-warning" />
              </div>
              <div className="text-2xl font-bold text-status-warning">
                {structure.demandTeamWeeks || 0}
              </div>
              <div className="text-xs text-muted-foreground/60">Demand (TWs)</div>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm" className="text-center">
              <div className="flex items-center justify-center mb-2">
                <Target className="w-4 h-4 text-status-success" />
              </div>
              <div className="text-2xl font-bold text-status-success">
                {structure.supplyTeamWeeks || 0}
              </div>
              <div className="text-xs text-muted-foreground/60">Supply (TWs)</div>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm" className="text-center">
              <div className="flex items-center justify-center mb-2">
                <DollarSign className="w-4 h-4 text-accent-blue" />
              </div>
              <div className="text-2xl font-bold text-accent-blue">
                {formatCurrencyCompact(structure.supplyAnnualCost || 0)}
              </div>
              <div className="text-xs text-muted-foreground/60">Annual Cost</div>
            </GlassCard>

            <GlassCard variant="subtle" padding="sm" className="text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingUp className="w-4 h-4 text-accent-violet" />
              </div>
              <div className={`text-2xl font-bold ${
                utilization >= 90 ? 'text-status-success' :
                utilization >= 70 ? 'text-status-warning' :
                'text-status-error'
              }`}>
                {formatPercent(utilization, { decimals: 0 })}
              </div>
              <div className="text-xs text-muted-foreground/60">Utilization</div>
            </GlassCard>
          </div>
        </div>

        {/* Related EPICs */}
        <GlassCard variant="subtle" padding="md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-blue/10">
                <Target className="w-5 h-5 text-accent-blue" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  Related EPICs
                </h3>
                <p className="text-sm text-muted-foreground/60">
                  {epicsCount} EPIC{epicsCount !== 1 ? 's' : ''} associated with this structure
                </p>
              </div>
            </div>
            {epicsCount > 0 && (
              <GlassButton
                variant="secondary"
                size="sm"
                onClick={handleViewEpics}
                rightIcon={<ArrowRight className="w-4 h-4" />}
              >
                View EPICs
              </GlassButton>
            )}
          </div>
        </GlassCard>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/10">
          <GlassButton variant="ghost" onClick={onClose}>
            Close
          </GlassButton>
          {onEdit && (
            <GlassButton
              variant="primary"
              leftIcon={<Edit className="w-4 h-4" />}
              onClick={onEdit}
            >
              Edit Structure
            </GlassButton>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InvestibleStructureDetailModal;
